import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { PosDeviceProvider } from './context/PosDeviceContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Orders from './components/Orders';
import CreateOrder from './components/CreateOrder';
import OrderDetails from './components/OrderDetails';
import StoreManagerDashboard from './components/StoreManagerDashboard';
import AccountantView from './components/AccountantView';
import ExpensesReview from './components/ExpensesReview';
import OrderApprovals from './components/OrderApprovals';
import AdminAnalytics from './components/AdminAnalytics';
import Users from './components/Users';
import Agents from './components/Agents';
import Caterers from './components/Caterers';
import CatererAccount from './components/CatererAccount';
import GrazingAdmin from './components/GrazingAdmin';
import Desserts from './components/Desserts';
import CupCakes from './components/CupCakes';
import Plants from './components/Plants';
import Flavours from './components/Flavours';
import CateringMenu from './components/CateringMenu';
import CateringPlannerPage from './cateringPlanner/CateringPlannerPage';
import PosHub from './components/PosHub';
import PosStoreMenus from './components/PosStoreMenus';
import PosInventory from './components/PosInventory';
import LabelPrintTest from './components/LabelPrintTest';
import OwnerHub from './components/OwnerHub';
import ProtectedRoute from './components/ProtectedRoute';
import PosDeviceRoute from './components/PosDeviceRoute';
import PosDeviceEnroll from './components/PosDeviceEnroll';
import AdminShell from './components/AdminShell';
import Footer from './components/Footer';
import MobileBottomNav from './components/MobileBottomNav';
import PwaInstallBanner from './components/PwaInstallBanner';
import './App.css';
import './theme-lux.css';

function LuxBodyClass() {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isPosSurface =
    location.pathname === '/pos' ||
    location.pathname.startsWith('/pos/') ||
    location.pathname === '/login' ||
    location.pathname === '/catering-planner';
  const useAdminShell = user?.role === 'Admin' && !isPosSurface;

  useEffect(() => {
    const useLux = location.pathname !== '/catering-planner';
    document.body.classList.toggle('body-theme-lux', useLux);
    document.body.classList.toggle('has-mobile-bottom-nav', !useAdminShell);
    document.body.classList.toggle('has-admin-shell', useAdminShell);
    return () => {
      document.body.classList.remove('body-theme-lux');
      document.body.classList.remove('has-mobile-bottom-nav');
      document.body.classList.remove('has-admin-shell');
    };
  }, [location.pathname, useAdminShell]);
  return null;
}

function AdminChrome({ children }) {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const isPosSurface =
    location.pathname === '/pos' ||
    location.pathname.startsWith('/pos/') ||
    location.pathname === '/login' ||
    location.pathname === '/catering-planner';

  if (user?.role === 'Admin' && !isPosSurface) {
    return <AdminShell>{children}</AdminShell>;
  }
  return children;
}

function AppRoutes() {
  return (
    <>
      <LuxBodyClass />
      <div className="App">
        <div className="App-content">
          <AdminChrome>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/pos/enroll" element={<PosDeviceEnroll />} />
            <Route path="/catering-planner" element={<CateringPlannerPage />} />
            <Route path="/catering-builder" element={<Navigate to="/catering-planner" replace />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/create"
              element={
                <ProtectedRoute allowedRoles={['Order Creator', 'Admin', 'Cashier', 'Franchise Admin', 'Store Manager', 'Accountant']}>
                  <CreateOrder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:orderId"
              element={
                <ProtectedRoute>
                  <OrderDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/store-manager"
              element={
                <ProtectedRoute allowedRoles={['Store Manager', 'Admin', 'Franchise Admin']}>
                  <StoreManagerDashboard />
                </ProtectedRoute>
              }
            />
              <Route
                path="/accountant"
                element={
                <ProtectedRoute allowedRoles={['Accountant', 'Admin']}>
                  <AccountantView />
                </ProtectedRoute>
                }
              />
              <Route
                path="/expenses"
                element={
                <ProtectedRoute allowedRoles={['Accountant', 'Admin']}>
                  <ExpensesReview />
                </ProtectedRoute>
                }
              />
              <Route
                path="/order-approvals"
                element={
                <ProtectedRoute allowedRoles={['Admin']}>
                  <OrderApprovals />
                </ProtectedRoute>
                }
              />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute allowedRoles={['Admin']}>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={['Admin']}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pos-menus"
              element={
                <ProtectedRoute allowedRoles={['Admin']}>
                  <PosStoreMenus />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pos-inventory"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Store Manager', 'Franchise Admin', 'Accountant']}>
                  <PosInventory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Order Creator']}>
                  <Agents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/caterers"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Order Creator', 'Accountant']}>
                  <Caterers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/caterers/:id/account"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Order Creator', 'Accountant']}>
                  <CatererAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/grazing-tables"
              element={
                <ProtectedRoute allowedRoles={['Admin']}>
                  <GrazingAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/desserts"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Order Creator']}>
                  <Desserts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cup-cakes"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Order Creator']}>
                  <CupCakes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plants"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Order Creator']}>
                  <Plants />
                </ProtectedRoute>
              }
            />
            <Route
              path="/flavours"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Order Creator']}>
                  <Flavours />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catering-menu"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Order Creator']}>
                  <CateringMenu />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pos"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Store Manager', 'Cashier', 'Franchise Admin']}>
                  <PosDeviceRoute>
                    <PosHub />
                  </PosDeviceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pos/label-print-test"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Store Manager', 'Cashier', 'Franchise Admin']}>
                  <PosDeviceRoute>
                    <LabelPrintTest />
                  </PosDeviceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/owner"
              element={
                <ProtectedRoute allowedRoles={['Admin']}>
                  <OwnerHub />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AdminChrome>
        </div>
        <Footer />
        <MobileBottomNav />
        <PwaInstallBanner />
      </div>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <PosDeviceProvider>
        <Router>
          <AppRoutes />
        </Router>
      </PosDeviceProvider>
    </AuthProvider>
  );
}

export default App;

